import { startOfMonth, subMonths } from 'date-fns';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import PageEndMarker from '~/components/PageEndMarker';
import { SkeletonLoader } from '~/components/data-display/SkeletonLoader/SkeletonLoader';
import RadioGroup from '~/components/inputs/RadioGroup/RadioGroup';
import Text from '~/components/typographies/Text';
import { AnalyticsEvents } from '~/services/analytics';
import { TableFiltersProvider, useFilterState } from '~/state/tableFilters';
import { Counterpart } from '~/types/counterparts';
import { Document, DocumentsQueryFilters, DocumentStatus } from '~/types/document';
import { useDocumentsForTransactionRegistration } from '~/views/bookkeeping/transactions/BookkeepingDocumentSelectionDialog/useDocumentsForTransactionRegistration';
import styles from './DocumentSearch.module.scss';
import DocumentSearchCard from './DocumentSearchCard';
import DocumentSearchFilters from './DocumentSearchFilters';

const getDefaultFilters = (overrides: { selectedCounterpart?: Pick<Counterpart, 'id' | 'companyName'> | null }) => {
  const startDate = startOfMonth(subMonths(new Date(), 6));
  return {
    defaultValues: {
      hasOpenBalance: true,
      search: '',
      totalAmount: null,
      creationDate: [startDate.toISOString(), null],
      documentTypes: [],
      currencies: [],
      paymentStatus: null,
      direction: null,
      counterpart: overrides.selectedCounterpart ? [overrides.selectedCounterpart.id] : null,
      status: [DocumentStatus.Created, DocumentStatus.Sent, DocumentStatus.Delivered],
    },
    analytics: {
      search: AnalyticsEvents.BILLS_SEARCH_BAR_USED,
      filter: AnalyticsEvents.BILLS_FILTER_USED,
    },
  };
};

const SearchResults = ({
  filters,
  onDocumentSelect,
  selectedDocument,
}: {
  filters: DocumentsQueryFilters;
  onDocumentSelect: (document: Document) => void;
  selectedDocument?: Document | null;
}) => {
  const { t } = useTranslation('bookkeeping', { keyPrefix: 'transactions.associate_document' });
  const documentsQuery = useDocumentsForTransactionRegistration(filters);

  const results = documentsQuery.data?.pages.flatMap(({ documents }) => documents) ?? [];

  if (documentsQuery.isLoading) {
    return <SkeletonLoader height={100} />;
  }

  if (results.length === 0) {
    return (
      <div className={styles.emptyState}>
        <Text variant="body2" weight="regular">
          {t('no_documents_found')}
        </Text>
      </div>
    );
  }

  const handleRadioChange = (documentId: string) => {
    const selectedDoc = results.find((doc) => doc.id === documentId);
    if (selectedDoc) {
      onDocumentSelect(selectedDoc);
    }
  };

  return (
    <div className={styles.searchResults}>
      <RadioGroup
        value={selectedDocument?.id || ''}
        onChange={handleRadioChange}
        ariaLabel={t('select_document_aria_label')}
      >
        {results.map((document) => (
          <DocumentSearchCard key={document.id} document={document} />
        ))}
      </RadioGroup>
      <PageEndMarker height={72} query={documentsQuery} />
    </div>
  );
};

interface Props {
  onDocumentSelect: (document: Document) => void;
  selectedDocument?: Document | null;
  selectedCounterpart?: Pick<Counterpart, 'id' | 'companyName'> | null;
}

interface DocumentSearchContentProps {
  onDocumentSelect: (document: Document) => void;
  selectedDocument?: Document | null;
}

const DocumentSearchContent = ({ onDocumentSelect, selectedDocument }: DocumentSearchContentProps) => {
  const filters = useFilterState<DocumentsQueryFilters>();

  return (
    <div className={styles.root}>
      <DocumentSearchFilters filters={filters} />
      <SearchResults filters={filters} onDocumentSelect={onDocumentSelect} selectedDocument={selectedDocument} />
    </div>
  );
};

export const DocumentSearch = ({ onDocumentSelect, selectedDocument, selectedCounterpart }: Props) => {
  const defaultFilters = useMemo(() => getDefaultFilters({ selectedCounterpart }), [selectedCounterpart]);
  return (
    <TableFiltersProvider {...defaultFilters}>
      <DocumentSearchContent onDocumentSelect={onDocumentSelect} selectedDocument={selectedDocument} />
    </TableFiltersProvider>
  );
};
